import { Add as AddIcon, Close as CloseIcon, CheckSharp } from '@mui/icons-material';
import { Box, Button, Card, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import React from 'react';

interface OpportunityModalProps {
  isOpen: boolean;
  formData: {
    name: string;
    content: string;
    knowledgeData: string[];
  };
  onClose: () => void;
  onFormDataChange: (data: { name: string; content: string; knowledgeData: string[] }) => void;
  onAddKnowledgeData: () => void;
  onRemoveKnowledgeData: (index: number) => void;
  onKnowledgeDataChange: (index: number, value: string) => void;
  isDeleteModalOpen: boolean;
  onDeleteModalClose: () => void;
  onDeleteConfirm: () => void;
  onSave: () => void;
}

const OpportunityModal: React.FC<OpportunityModalProps> = ({
  isOpen,
  formData,
  onClose,
  onFormDataChange,
  onAddKnowledgeData,
  onRemoveKnowledgeData,
  onKnowledgeDataChange,
  isDeleteModalOpen,
  onDeleteModalClose,
  onDeleteConfirm,
  onSave,
}) => {
  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby="add-opportunity-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            width: { xs: '95%', sm: '85%', md: '600px', lg: '700px' },
            maxWidth: '95vw',
            maxHeight: { xs: '95vh', sm: '90vh' },
            bgcolor: 'background.paper',
            borderRadius: { xs: 1, sm: 2 },
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              bgcolor: '#1b54f8',
              color: 'white',
              p: { xs: 2, sm: 3 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 500,
              }}
            >
              AI Opportunity
            </Typography>
            {/* <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{ color: 'white' }}
                        >
                            <CloseIcon />
                        </IconButton> */}
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              overflow: 'auto',
              flex: 1,
            }}
          >
            <Stack spacing={{ xs: 2.5, sm: 3 }}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    mb: 1,
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    fontWeight: 500,
                  }}
                >
                  Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="New AI Opportunity"
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: 1, sm: 2 },
                      bgcolor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      '& fieldset': { border: 'none' },
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: '0 0 0 2px rgba(66, 133, 244, 0.2)',
                      },
                    },
                  }}
                />
              </Box>

              {/* Content Field */}
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    mb: 1,
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    fontWeight: 500,
                  }}
                >
                  Content
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Enter Content"
                  variant="outlined"
                  value={formData.content}
                  onChange={(e) => onFormDataChange({ ...formData, content: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: 1, sm: 2 },
                      bgcolor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      '& fieldset': { border: 'none' },
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: '0 0 0 2px rgba(66, 133, 244, 0.2)',
                      },
                    },
                  }}
                />
              </Box>

              {/* Knowledge Data Section */}
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    mb: 1,
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    fontWeight: 500,
                  }}
                >
                  Knowledge Data
                </Typography>
                <Stack spacing={{ xs: 1, sm: 1.5 }}>
                  {formData.knowledgeData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 1.5 },
                        alignItems: 'flex-start',
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Enter Knowledge Data"
                        variant="outlined"
                        value={item}
                        onChange={(e) => onKnowledgeDataChange(index, e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: 1, sm: 2 },
                            bgcolor: '#f8f9fa',
                            border: '1px solid #e0e0e0',
                            '& fieldset': { border: 'none' },
                            '&:hover': { bgcolor: '#f5f5f5' },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              boxShadow: '0 0 0 2px rgba(66, 133, 244, 0.2)',
                            },
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => onRemoveKnowledgeData(index)}
                        disabled={formData.knowledgeData.length === 1}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          bgcolor: '#1b54f8',
                          color: 'white',
                          borderRadius: { xs: 1, sm: 2 },
                          '&:hover': { bgcolor: '#1b54f8' },
                          '&.Mui-disabled': {
                            bgcolor: '#e0e0e0',
                            color: '#9e9e9e',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      </IconButton>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 1,
                    }}
                  >
                    <Button
                      onClick={onSave}
                      variant="contained"
                      sx={{
                        minWidth: { xs: 80, sm: 90 }, // Increased width for better touch targets
                        height: { xs: 36, sm: 40 },
                        bgcolor: '#1b54f8',
                        color: 'white',
                        borderRadius: { xs: 1, sm: 2 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#3367d6' },
                      }}
                    >
                      Save
                    </Button>

                    <IconButton
                      onClick={onAddKnowledgeData}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        bgcolor: '#1b54f8',
                        color: 'white',
                        borderRadius: { xs: 1, sm: 2 },
                        '&:hover': { bgcolor: '#3367d6' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        aria-labelledby="delete-opportunity-modal"
      >
        <Card
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: '#1a73e8',
            color: 'white',
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="h6">Delete AI opportunities?</Typography>
            <IconButton onClick={onDeleteModalClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              bgcolor: 'white',
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="text"
              onClick={onDeleteModalClose}
              sx={{
                bgcolor: '#1a73e8',
                color: 'white',
              }}
            >
              <CloseIcon fontSize="small" />
              No
            </Button>
            <Button
              variant="text"
              onClick={onDeleteConfirm}
              sx={{
                color: 'white',
                bgcolor: '#1a73e8',
              }}
            >
              <CheckSharp fontSize="small" />
              Yes
            </Button>
          </Box>
        </Card>
      </Modal>
    </>
  );
};

export default OpportunityModal;
